import { Text } from '@clay-run/glaze/text';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { Gear } from '@phosphor-icons/react/dist/icons/Gear';
import { ArrowLeft } from '@phosphor-icons/react/dist/icons/ArrowLeft';
import { ClockCounterClockwise } from '@phosphor-icons/react/dist/icons/ClockCounterClockwise';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../../_shared/components';
import { useCurrentUser } from '../../_shared/hooks';
import { useAppBasePath } from '../../_shared/base-path';
import { useLeadScannerStore, activeScanIdFromView } from '../store';
import { useScansQuery } from '../hooks/useScanApi';
import { useIsWideViewport } from '../hooks/useIsWideViewport';
import ActiveScanPanel from './ActiveScanPanel';
import Home from './Home';
import RepEventMapper from './RepEventMapper';

/**
 * The shared responsive shell (R7, U4): real `window.matchMedia('(min-width:
 * 960px)')` viewport query (`useIsWideViewport`, prototype's `WIDE_MQ`,
 * docs/lead-scanner-prototype.html ~line 728), one `view` route
 * (`useLeadScannerStore`) driving Home / a scan's review screen / Settings.
 * Below 960px, Home and the scan-review screen are separate stacked phone
 * screens; at 960px+ they fuse into one master-detail screen — Home in the
 * left rail, the active scan on the right (prototype's `.swrap`/`.srail`/
 * `.sdetail`, ~lines 337-341, 1550-1573).
 *
 * `ActiveScanPanel` (`./ActiveScanPanel.tsx`) renders `ScanReview` for every
 * non-`processing` scan and a skeleton state for `processing` ones —
 * extracted out of this file once the unified Scans page (`ScansPage.tsx`,
 * plan plans/2026-08-26-001-lead-scanner-scans-page-plan.md) needed the same
 * wide/narrow master-detail panel.
 *
 * The old admin-only "All Submissions" view (U18, R20) is gone — replaced by
 * a real router route (`path="scans"` in `index.tsx`) rendering `ScansPage`,
 * reachable from the always-visible "Scans" nav entry below (no admin gate:
 * see `api.ts`'s file-level comment for why cross-rep visibility is no
 * longer role-gated).
 */

export default function ViewSwitcher() {
  const isWide = useIsWideViewport();
  const { user } = useCurrentUser();
  const currentUserEmail = user?.ownerEmail ?? user?.email ?? null;
  const navigate = useNavigate();
  const appBase = useAppBasePath();

  const view = useLeadScannerStore((s) => s.view);
  const goHome = useLeadScannerStore((s) => s.goHome);
  const goSettings = useLeadScannerStore((s) => s.goSettings);
  const activeScanId = activeScanIdFromView(view);

  // The SAME hook Home.tsx uses (same key → shared cache, no second round
  // trip) — and, critically, the same `refetchInterval`. On a narrow viewport
  // this is the ONLY observer once a scan is open, because Home unmounts; a
  // locally-declared query here without the poll rule would leave a
  // `processing` row frozen on screen forever.
  const scansQuery = useScansQuery(currentUserEmail);

  const activeScan = activeScanId ? (scansQuery.data ?? []).find((s) => s.id === activeScanId) ?? null : null;

  if (view === 'settings') {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <IconButton
            icon={ArrowLeft}
            aria-label="Back to scanning"
            variant="ghost"
            className="self-start"
            onClick={goHome}
          />
          <ThemeToggle />
        </div>
        <RepEventMapper />
      </div>
    );
  }

  const header = (
    <div className="flex items-center justify-between gap-2">
      <Text size="sm" weight="semibold">
        Lead Scanner
      </Text>
      <div className="flex items-center gap-2">
        <Text size="xs" variant="tertiary">
          {user?.email ?? ''}
        </Text>
        <ThemeToggle />
        {/* Always-visible — the old admin-only gate on cross-rep visibility
            (U18, R20) is a deliberate, documented reversal (`api.ts`'s
            file-level comment): any authenticated rep can open the Scans
            page and clear its "my scans" filter to see everyone's scans. */}
        <IconButton
          icon={ClockCounterClockwise}
          aria-label="Scans"
          variant="ghost"
          onClick={() => navigate(`${appBase}/scans`)}
        />
        <IconButton icon={Gear} aria-label="Settings" variant="ghost" onClick={goSettings} />
      </div>
    </div>
  );

  if (isWide) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {/* No CSS breakpoint here — `isWide` (the real `matchMedia` result)
            already gated this whole branch, so the two-column layout is
            unconditional once we're inside it. */}
        <div className="grid grid-cols-[minmax(300px,360px)_minmax(0,1fr)] gap-4">
          <Home />
          <div>
            {activeScanId ? (
              activeScan ? (
                <ActiveScanPanel scan={activeScan} />
              ) : (
                <Text size="sm" variant="tertiary">
                  Loading…
                </Text>
              )
            ) : (
              <Text size="sm" variant="tertiary">
                Start a new scan below, or open the Scans page to review an existing draft.
              </Text>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (activeScanId) {
    return (
      <div className="flex flex-col gap-4">
        <IconButton
          icon={ArrowLeft}
          aria-label="Back to Home"
          variant="ghost"
          className="self-start"
          onClick={goHome}
        />
        {activeScan ? (
          <ActiveScanPanel scan={activeScan} />
        ) : (
          <Text size="sm" variant="tertiary">
            Loading…
          </Text>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {header}
      <Home />
    </div>
  );
}
